import React from 'react';
import { useNavigate } from 'react-router-dom';

import { TaskDetail } from '@/components/tasks/task-detail';
import { Badge } from '@/components/ui/catalyst/badge';
import {
  Dialog,
  DialogTitle,
  RightDialogContent,
} from '@/components/ui/dialog';
import { useGetTasksQuery } from '@/services/api/tasksService';
import { Task, TaskSearchResults } from '@/services/types/client-intake-types';
import {
  CanonicalTaskStatus,
  CanonicalTaskType,
  TaskStatusColors,
} from '@/services/types/task-types';

import { LatestTaskUpdate } from './LatestTaskUpdate';

// Utility types for grouping
interface ProviderGroup {
  providerId: string;
  providerName: string;
  tasks: Task[];
}

interface MatterGroup {
  matterId: string;
  matterName: string;
  providers: ProviderGroup[];
}

interface FirmGroup {
  firmId: string;
  firmName: string;
  matters: MatterGroup[];
}

const groupTasksByFirmMatterProvider = (tasks: Task[]): FirmGroup[] => {
  // Build nested maps using reduce
  const byFirm = tasks.reduce((acc, task) => {
    const firmId = task.firm;
    const firmName = task.firm_name;
    const matterId = task.matter;
    const matterName = task.matter_name;
    const providerId = task.medical_note?.id ?? 'unknown-provider';
    const providerName = task.medical_note?.name ?? 'Unspecified Provider';

    if (!acc.has(firmId)) {
      acc.set(firmId, {
        name: firmName,
        matters: new Map<
          string,
          {
            name: string;
            providers: Map<string, { name: string; tasks: Task[] }>;
          }
        >(),
      });
    }
    const firmEntry = acc.get(firmId)!;

    if (!firmEntry.matters.has(matterId)) {
      firmEntry.matters.set(matterId, {
        name: matterName,
        providers: new Map<string, { name: string; tasks: Task[] }>(),
      });
    }
    const matterEntry = firmEntry.matters.get(matterId)!;

    if (!matterEntry.providers.has(providerId)) {
      matterEntry.providers.set(providerId, {
        name: providerName,
        tasks: [],
      });
    }
    const providerEntry = matterEntry.providers.get(providerId)!;
    providerEntry.tasks.push(task);

    return acc;
  }, new Map<string, { name: string; matters: Map<string, { name: string; providers: Map<string, { name: string; tasks: Task[] }> }> }>());

  // Transform maps to arrays and sort using array methods
  return Array.from(byFirm.entries())
    .map(([firmId, firmVal]) => {
      const matters: MatterGroup[] = Array.from(firmVal.matters.entries())
        .map(([matterId, matterVal]) => {
          const providers: ProviderGroup[] = Array.from(
            matterVal.providers.entries(),
          )
            .map(([providerId, providerVal]) => ({
              providerId,
              providerName: providerVal.name,
              tasks: providerVal.tasks,
            }))
            .sort((a, b) => a.providerName.localeCompare(b.providerName));

          return {
            matterId,
            matterName: matterVal.name,
            providers,
          };
        })
        .sort((a, b) => a.matterName.localeCompare(b.matterName));

      return {
        firmId,
        firmName: firmVal.name,
        matters,
      };
    })
    .sort((a, b) => a.firmName.localeCompare(b.firmName));
};

const StatusBadge: React.FC<{ status: CanonicalTaskStatus }> = ({ status }) => {
  const color = TaskStatusColors[status] || 'bg-gray-100 text-gray-700';
  return (
    <span
      className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${color}`}
    >
      {status}
    </span>
  );
};

const formatDate = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString();
};

export const MedicalRecordsDashboard: React.FC = () => {
  const navigate = useNavigate();

  // Right drawer selected task
  const [selectedTask, setSelectedTask] = React.useState<Task | null>(null);

  // Fetch medical-records tasks (use canonical type)
  const { data, isLoading, isError } = useGetTasksQuery({
    canonical_types: [CanonicalTaskType.MEDICAL_RECORDS],
    limit: 500,
    offset: 0,
    ordering: '-order,-modified_at',
  });

  const tasks: Task[] = (data as TaskSearchResults | undefined)?.results ?? [];

  // Filter to outstanding statuses client-side for immediate clarity
  const outstandingStatuses: CanonicalTaskStatus[] = [
    CanonicalTaskStatus.PENDING,
    CanonicalTaskStatus.IN_PROGRESS,
    CanonicalTaskStatus.BLOCKED,
  ];
  const outstandingTasks = tasks.filter(
    (t) =>
      t.status?.canonical_status &&
      outstandingStatuses.includes(t.status.canonical_status),
  );

  const grouped = groupTasksByFirmMatterProvider(outstandingTasks);

  const handleOpenTask = (task: Task) => {
    setSelectedTask(task);
  };

  const handleCloseTask = () => {
    setSelectedTask(null);
  };

  if (isLoading) {
    return (
      <div className="p-6 text-sm text-gray-600">Loading medical records…</div>
    );
  }
  if (isError) {
    return (
      <div className="p-6 text-sm text-red-600">
        Failed to load medical records tasks.
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-gray-900">
          Medical Records Dashboard
        </h1>
        <Badge color="blue">{outstandingTasks.length} Outstanding</Badge>
      </div>

      {grouped.length === 0 && (
        <div className="text-sm text-gray-600">
          No outstanding medical records tasks.
        </div>
      )}

      <div className="space-y-6">
        {grouped.map((firm) => (
          <div key={firm.firmId} className="border border-gray-200 rounded-lg">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900">
                  {firm.firmName}
                </span>
                <span className="text-xs text-gray-500">
                  {firm.matters.length} matters
                </span>
              </div>
              <Badge>
                {firm.matters.reduce(
                  (acc, m) =>
                    acc + m.providers.reduce((a, p) => a + p.tasks.length, 0),
                  0,
                )}{' '}
                tasks
              </Badge>
            </div>

            {firm.matters.map((matter) => (
              <div
                key={matter.matterId}
                className="border-b last:border-b-0 border-gray-200"
              >
                <div className="px-4 py-3 flex items-center justify-between bg-white">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="text-sm font-medium text-blue-700 hover:underline"
                      onClick={() => navigate(`/matters/${matter.matterId}`)}
                      aria-label={`Open matter ${matter.matterName}`}
                    >
                      {matter.matterName}
                    </button>
                    <span className="text-xs text-gray-500">
                      {matter.providers.length} providers
                    </span>
                  </div>
                </div>

                <div className="divide-y divide-gray-100">
                  {matter.providers.map((provider) => (
                    <div key={provider.providerId} className="bg-white">
                      <div className="px-4 py-2 bg-gray-50 flex items-center justify-between">
                        <span className="text-sm text-gray-800">
                          {provider.providerName}
                        </span>
                        <span className="text-xs text-gray-500">
                          {provider.tasks.length} tasks
                        </span>
                      </div>

                      <ul className="px-2 py-2 space-y-2">
                        {provider.tasks.map((task) => (
                          <li key={task.id}>
                            <button
                              type="button"
                              className="w-full text-left"
                              onClick={() => handleOpenTask(task)}
                              aria-label={`Open task ${task.name}`}
                            >
                              <div className="rounded-md border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-colors p-3">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-medium text-gray-900 truncate">
                                        {task.name}
                                      </span>
                                      {task.status?.canonical_status && (
                                        <StatusBadge
                                          status={task.status.canonical_status}
                                        />
                                      )}
                                    </div>
                                    {task.description && (
                                      <p className="mt-1 text-xs text-gray-700 whitespace-pre-wrap">
                                        {task.description}
                                      </p>
                                    )}
                                    {/* Latest update text */}
                                    <LatestTaskUpdate
                                      matterId={task.matter}
                                      taskId={task.id}
                                    />

                                    <div className="mt-1 flex items-center gap-3 text-[11px] text-gray-500">
                                      {task.due_date && (
                                        <span>
                                          Due {formatDate(task.due_date)}
                                        </span>
                                      )}
                                      {task.medical_note
                                        ?.date_of_records_request && (
                                        <span>
                                          Requested{' '}
                                          {formatDate(
                                            task.medical_note
                                              .date_of_records_request,
                                          )}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex flex-col items-end gap-1 text-[11px] text-gray-500">
                                    <span>Firm: {task.firm_name}</span>
                                    <span>
                                      Updated {formatDate(task.modified_at)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {selectedTask && (
        <Dialog open onOpenChange={handleCloseTask} modal={false}>
          <RightDialogContent className="bg-white h-[95vh] overflow-hidden flex flex-col">
            <DialogTitle className="sr-only">Task Details</DialogTitle>
            <TaskDetail
              taskId={selectedTask.id}
              matterId={selectedTask.matter}
              onClose={handleCloseTask}
              isModal={false}
              task={selectedTask}
            />
          </RightDialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default MedicalRecordsDashboard;
